import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import debounce from 'lodash/debounce';
import React, { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { WidgetDTO } from '../../../AccountPerformanceReporting/api/useWidgetsQuery';
import { WIDGETS_WITH_GROUP_SELECTION } from '../../../AccountPerformanceReporting/components/DesktopWidgets/DesktopWidgets.meta';
import { ExcludeWidgetModalContent } from '../../../AccountPerformanceReporting/components/ExcludeWidgetModal/ExcludeWidgetModal';
import { EmptyState } from '../../../CommonComponents/OPL/AsyncStates/EmptyState/EmptyState';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { IconButton } from '../../../CommonComponents/OPL/IconButton/IconButton';
import { ListItem } from '../../../CommonComponents/OPL/List/ListItem';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from '../../../CommonComponents/OPL/Menu/Menu';
import { WidgetSlugDTO } from '../../api/useDefaultTemplateQuery';
import { WidgetContent } from '../WidgetContent/WidgetContent';

interface Props {
  activeTab: WidgetSlugDTO;
}

export const Widgets = ({ activeTab }: Props) => {
  const { setValue, watch, getValues } = useFormContext();

  const widgets = watch('widgets')?.[activeTab];

  const [activeWidget, setActiveWidget] = useState<WidgetDTO | null>(null);

  const [widgetToExclude, setWidgetToExclude] = useState<WidgetDTO | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveWidget(widgets.included.find(({ id }) => id === active.id) || null);
  };

  const handleDragOver = ({ active, over }: DragEndEvent) => {
    const currentWidgets = getValues(`widgets.${activeTab}`);
    const update = {
      ...currentWidgets,
      included: arrayMove(
        currentWidgets.included,
        currentWidgets.included.findIndex(({ id }) => active?.id === id),
        currentWidgets.included.findIndex(({ id }) => id === over?.id),
      ),
    };

    setValue(`widgets.${activeTab}`, update);
  };

  const debouncedHandleDragOver = useRef(debounce(handleDragOver, 300)).current;

  const handleDragEnd = () => {
    setActiveWidget(null);

    setValue(`widgets.${activeTab}`, widgets);
  };

  const toggleCardWidth = (id: string) => {
    const nextWidgets = {
      ...widgets,
      included: widgets.included.map(widget =>
        widget.id === id ? { ...widget, isFullWidth: !widget.isFullWidth } : widget,
      ),
    };

    setValue(`widgets.${activeTab}`, nextWidgets);
  };

  if (widgets.included.length === 0) {
    return (
      <Box p="32">
        <EmptyState message="No customizable widgets available for this tab." />
      </Box>
    );
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragOver={debouncedHandleDragOver}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={widgets.included} strategy={() => null}>
          <Box display="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {widgets.included.map(widget => (
              <DraggableCard
                key={widget.id}
                toggleCardWidth={toggleCardWidth}
                isMutatingWidgets={false}
                onExcludeWidget={() => setWidgetToExclude(widget)}
                {...widget}
              />
            ))}
          </Box>
        </SortableContext>

        <DragOverlay>
          {activeWidget && <DraggableCard isOverlay key={activeWidget.id} {...activeWidget} />}
        </DragOverlay>
      </DndContext>

      {widgetToExclude && (
        <ExcludeWidgetModalContent
          widgetToExclude={widgetToExclude}
          widgets={widgets}
          onClose={() => setWidgetToExclude(null)}
          onSave={nextWidgets => {
            setValue(`widgets.${activeTab}`, nextWidgets);
          }}
        />
      )}
    </>
  );
};

interface DraggableCardProps extends WidgetDTO {
  onExcludeWidget?(): void;
  isOverlay?: boolean;
  toggleCardWidth?(id: string): void;
  isMutatingWidgets?: boolean;
}

const DraggableCard = ({
  id,
  title,
  isOverlay,
  isFullWidth,
  isWidthAdjustable,
  toggleCardWidth,
  isMutatingWidgets,
  onExcludeWidget,
}: DraggableCardProps) => {
  const { setNodeRef, attributes, listeners, isDragging, transform, transition } = useSortable({
    id,
  });

  const { watch } = useFormContext();

  const templateId = watch('id');

  const isDefaultTemplate = templateId === 'default';

  const readOnly = isMutatingWidgets || isOverlay || isDragging || isDefaultTemplate;

  return (
    <Box
      key={id}
      ref={setNodeRef}
      css={{
        gridColumn: isFullWidth ? 'span 2' : 'unset',
        opacity: isDragging ? 0.5 : 1,
        transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : 'none',
        transition,
      }}
    >
      {WIDGETS_WITH_GROUP_SELECTION.includes(id) && (
        <Box
          id={`group-selection--portal-target--${id}`}
          style={{
            display: isDragging ? 'none' : 'block',
          }}
        />
      )}

      <Box
        css={{
          position: 'relative',
          height: '100%',
          '> *': {
            height: '100%',
          },
        }}
      >
        {!isDefaultTemplate && (
          <Box
            style={{
              display: 'flex',
              position: 'absolute',
              top: 8,
              right: 8,
              gap: 2,
            }}
          >
            <IconButton
              disabled={readOnly}
              icon="fas fa-grip-vertical"
              color="neutral.500"
              variant="subtleInactive"
              css="cursor: grab; background: none !important"
              {...attributes}
              {...listeners}
            />

            <Menu>
              <MenuTrigger disabled={readOnly}>
                <IconButton aria-label="Menu" icon="fas fa-ellipsis-h" variant="subtleInactive" />
              </MenuTrigger>

              {!readOnly && (
                <MenuContent>
                  {isWidthAdjustable && (
                    <>
                      <MenuItem
                        onSelect={() => {
                          if (toggleCardWidth) {
                            toggleCardWidth(id);
                          }
                        }}
                      >
                        <ListItem icon="fas fa-arrows-h">{`Make ${
                          isFullWidth ? 'half' : 'full'
                        } width`}</ListItem>
                      </MenuItem>

                      <MenuSeparator />
                    </>
                  )}

                  <MenuItem onSelect={onExcludeWidget}>
                    <ListItem icon="far fa-trash-alt" variant="error">
                      Delete Card
                    </ListItem>
                  </MenuItem>
                </MenuContent>
              )}
            </Menu>
          </Box>
        )}

        <WidgetContent id={id} title={title} key={isFullWidth ? 'full' : 'half'} />
      </Box>
    </Box>
  );
};
